import { useEffect } from "react";
import { Mail, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";
import { LeadForm } from "../components/site/LeadForm";
import { MaskedLine, Reveal } from "../components/site/Reveal";
import { Sparkle } from "../components/site/Sparkle";
import { WA_DEFAULT, BRAND } from "../lib/site";

const channels = [
  {
    icon: Mail,
    title: "Email",
    value: BRAND.email,
    href: `mailto:${BRAND.email}`,
    testid: "contact-card-email",
    alt: "Email STUZIO. EDITFLOW",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp",
    value: BRAND.phoneDisplay,
    href: WA_DEFAULT,
    testid: "contact-card-whatsapp",
    alt: "WhatsApp STUZIO. EDITFLOW",
  },
  {
    icon: MapPin,
    title: "Office",
    value: "Varanasi, India — serving all of India",
    href: "https://maps.google.com/?q=Varanasi,India",
    testid: "contact-card-office",
    alt: "STUZIO. EDITFLOW office location",
  },
];

export default function Contact() {
  useEffect(() => {
    document.title = "Contact — STUZIO. EDITFLOW | Web Design Agency in Varanasi, India";
    return () => {
      document.title = "STUZIO. EDITFLOW — Website Development Company in India | Web Design Agency Varanasi";
    };
  }, []);

  return (
    <main className="relative pt-36 pb-24 sm:pb-32 overflow-hidden" data-testid="contact-page">
      <div className="glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-70" />
      <Sparkle className="absolute top-40 left-[6%]" size={16} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal y={16}>
          <p className="eyebrow mb-6" data-testid="contact-eyebrow">Contact</p>
        </Reveal>
        <h1 className="font-display font-light tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl" data-testid="contact-headline">
          <MaskedLine delay={0.05}>Let&apos;s build something</MaskedLine>
          <MaskedLine delay={0.17}>that <em className="italic text-shimmer">sells.</em></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-6 text-mute text-base sm:text-lg max-w-xl leading-relaxed">
            One message is all it takes. We reply fast — usually within the hour —
            and your free custom demo could be on your screen within days.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 space-y-4">
            {channels.map((c, i) => (
              <Reveal key={c.title} delay={0.08 * i}>
                <a
                  data-testid={c.testid}
                  href={c.href}
                  target={c.href.startsWith("mailto") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-label={c.alt}
                  className="group flex items-center gap-5 bg-panel border hairline p-6 card-lift"
                >
                  <span className="w-12 h-12 border border-brand/40 flex items-center justify-center text-brand group-hover:bg-brand group-hover:text-ink transition-all duration-500 shrink-0">
                    <c.icon size={19} strokeWidth={1.6} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-mono text-[10px] tracking-[0.28em] uppercase text-mute">{c.title}</span>
                    <span className="block text-white font-medium mt-1 truncate">{c.value}</span>
                  </span>
                  <ArrowUpRight size={16} className="text-mute group-hover:text-brand transition-colors duration-300 shrink-0" />
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="border border-brand/30 bg-brand/5 p-6" data-testid="contact-promise-card">
                <p className="font-display italic text-xl text-white leading-snug">
                  &ldquo;Message us today, and we&apos;ll tell you honestly whether you even need a new website.&rdquo;
                </p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-brand mt-3">{BRAND.founder} · Founder</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <div className="bg-panel border hairline p-7 sm:p-10 relative" data-testid="contact-form-card">
                <span className="absolute -top-px left-10 w-16 h-px bg-brand" aria-hidden="true" />
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-brand mb-2">Project Enquiry</p>
                <h2 className="font-display text-2xl sm:text-3xl text-white font-medium mb-7">Tell us about your project</h2>
                <LeadForm />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
